import { getContext } from 'configs/context';

import { apiBaseUrl, apiUploadUrl } from 'configs/constants';

const FOLDER_MIME_TYPE = 'application/vnd.picsart.folder';
const DRIVE_BASE = `${apiBaseUrl}/gw-v2/drive/v1`;
const FILES_URL = `${DRIVE_BASE}/files`;
const SEARCH_URL = `${FILES_URL}/search`;
const JSON_HEADERS = { 'Content-Type': 'application/json' };

export const HIDDEN_ATTRIBUTES = { privateVisibility: 'HIDDEN' };
export const PRESET_FOLDER_NAME = 'BatchPresets';
export const PRESET_CONTENT_LINK_ATTR = 'batch~presetContentLink';

export interface DriveFile {
  id: string;
  name: string;
  contentLink: string;
  type: 'FILE' | 'FOLDER';
  attributes?: Record<string, string>;
}

export type DriveResourceType = 'PHOTO' | 'VIDEO' | 'AUDIO' | 'OTHER';

let cachedFolderId: string | null = null;

function apiFetch(...args: Parameters<typeof fetch>): Promise<Response> {
  return getContext().utils.api.fetch(...args);
}

async function throwDriveError(response: Response, fallback: string): Promise<never> {
  let reason: string | undefined;
  try {
    const body = await response.clone().json();
    const raw = body?.reason ?? body?.response?.reason ?? body?.error?.code;
    if (typeof raw === 'string' && raw.length) reason = raw;
  } catch {
    // Body wasn't JSON — fall through to the status-based fallback message.
  }
  throw new Error(reason ?? `${fallback}: ${response.status}`);
}

export async function fetchBlobFromUrl(url: string): Promise<Blob> {
  const response = url.startsWith('blob:') || url.startsWith('data:') ? await fetch(url) : await apiFetch(url);
  if (!response.ok) await throwDriveError(response, 'Failed to fetch asset');
  return response.blob();
}

export async function uploadBlobToCdn(blob: Blob): Promise<string> {
  const formData = new FormData();
  formData.append('type', 'editing-temp');
  formData.append('file', blob);

  const response = await apiFetch(`${apiUploadUrl}/files`, { method: 'POST', body: formData });
  if (!response.ok) await throwDriveError(response, 'CDN upload failed');

  const { result } = await response.json();
  if (!result?.download_url) throw new Error('CDN upload failed: no download_url');

  return result.download_url as string;
}

export async function getOrCreateRootFolder(): Promise<string> {
  if (cachedFolderId) return cachedFolderId;

  const searchRes = await apiFetch(SEARCH_URL, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({
      mode: 'OWNED',
      filter: {
        op: 'and',
        filters: [
          { op: 'eq', field: 'type', value: 'FOLDER' },
          { op: 'eq', field: 'name', value: PRESET_FOLDER_NAME },
          { op: 'eq', field: 'attributes.privateVisibility', value: 'HIDDEN' },
        ],
      },
      limit: 1,
    }),
  });
  if (!searchRes.ok) await throwDriveError(searchRes, 'Failed to search folders');

  const { response: searchData } = await searchRes.json();
  const existing = (searchData.items as DriveFile[])[0];

  if (existing) {
    cachedFolderId = existing.id;
    return existing.id;
  }

  const createRes = await apiFetch(FILES_URL, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({
      name: PRESET_FOLDER_NAME,
      mimeType: FOLDER_MIME_TYPE,
      attributes: HIDDEN_ATTRIBUTES,
    }),
  });
  if (!createRes.ok) await throwDriveError(createRes, 'Failed to create folder');

  const { response: createData } = await createRes.json();
  cachedFolderId = createData.file.id;
  return createData.file.id;
}

export async function createSubfolder(name: string, parentFolderId: string): Promise<string> {
  const response = await apiFetch(FILES_URL, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({
      name,
      mimeType: FOLDER_MIME_TYPE,
      parentFolderId,
      attributes: HIDDEN_ATTRIBUTES,
    }),
  });
  if (!response.ok) await throwDriveError(response, 'Failed to create subfolder');
  const { response: data } = await response.json();
  return data.file.id;
}

export async function registerFileInDrive(name: string, cdnUrl: string, parentFolderId: string, resourceType: DriveResourceType = 'OTHER'): Promise<DriveFile> {
  const response = await apiFetch(FILES_URL, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({
      name,
      sourceUrl: cdnUrl,
      parentFolderId,
      content: { type: 'STANDALONE', resourceType },
      attributes: HIDDEN_ATTRIBUTES,
    }),
  });
  if (!response.ok) await throwDriveError(response, `Failed to register file "${name}"`);

  const { response: data } = await response.json();
  return data.file as DriveFile;
}

export async function updateFolderAttributes(folderId: string, attributes: Record<string, string>): Promise<void> {
  const response = await apiFetch(`${FILES_URL}/${folderId}`, {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify({ attributes }),
  });
  if (!response.ok) await throwDriveError(response, 'Failed to update folder attributes');
}

export async function searchSubfolders(parentFolderId: string): Promise<DriveFile[]> {
  const response = await apiFetch(SEARCH_URL, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({
      mode: 'BY_PARENT',
      filter: {
        op: 'and',
        filters: [
          { op: 'eq', field: 'parentFolderId', value: parentFolderId },
          { op: 'eq', field: 'type', value: 'FOLDER' },
          { op: 'eq', field: 'attributes.privateVisibility', value: 'HIDDEN' },
        ],
      },
      sort: [{ field: 'createdAt', direction: 'DESC' }],
      offset: 0,
      limit: 100,
    }),
  });
  if (!response.ok) await throwDriveError(response, 'Failed to load preset folders');

  const { response: searchData } = await response.json();
  return searchData.items as DriveFile[];
}

export async function deleteFile(fileId: string): Promise<void> {
  const response = await apiFetch(`${FILES_URL}/${fileId}`, {
    method: 'DELETE',
  });
  if (!response.ok && response.status !== 404) await throwDriveError(response, 'Failed to delete file');
}
